import {
    filteredText,
    isExcluded,
    openShadowRoots,
    type PageRules,
} from './snapshot'

/**
 * What the page says while an action of the agent runs (spec
 * `page-tools-improvements.md`, M3): the confirmations and errors a site
 * shows in a live region, a toast that holds one, the message of a field
 * that turned invalid. The action's result carries them word for word, so
 * the model learns the outcome without reading the page again and reports
 * what the page said, not what it supposes.
 */

const LIVE_REGIONS =
    '[role=status], [role=alert], [aria-live]:not([aria-live=off]), output'
/**
 * A chat transcript is a live region of messages, not the outcome of an
 * action: a site's support chat, or the preview of an agent echoing the
 * text the agent just wrote, read as a confirmation that was never given.
 */
const CHAT_LOG = '[role=log]'
const MAX_ANNOUNCEMENTS = 3
const MAX_CHARS = 200

/**
 * Starts listening; `said` answers what was said so far, in order, and
 * `stop` ends it with the same answer.
 */
export function listenForAnnouncements(rules: PageRules): {
    said: () => string[]
    stop: () => string[]
} {
    const regions = new Set<Element>()
    // The page's components announce in their open shadow roots too
    const scopes: (Document | ShadowRoot)[] = [
        document,
        ...openShadowRoots(document),
    ]
    // What the page already said: a region drawn again with the same text
    // (a component mounted anew) announces nothing
    const said = new Set(
        scopes.flatMap((scope) =>
            [...scope.querySelectorAll(LIVE_REGIONS)].map((region) =>
                filteredText(region, rules, MAX_CHARS),
            ),
        ),
    )
    const note = (node: Node | null) => {
        const element = node instanceof Element ? node : node?.parentElement
        if (!element) {
            return
        }
        const region = element.closest(LIVE_REGIONS)
        if (region) {
            regions.add(region)
        }
        if (node instanceof Element) {
            for (const inner of node.querySelectorAll(LIVE_REGIONS)) {
                regions.add(inner)
            }
        }
    }
    const observer = new MutationObserver((records) => {
        for (const record of records) {
            if (record.type !== 'attributes') {
                note(record.target)
                record.addedNodes.forEach(note)
                continue
            }
            // A field the site marks invalid points at its message
            const field = record.target as Element
            if (field.getAttribute('aria-invalid') !== 'true') {
                continue
            }
            const ids =
                field.getAttribute('aria-errormessage') ??
                field.getAttribute('aria-describedby') ??
                ''
            const root = field.getRootNode() as Document | ShadowRoot
            for (const id of ids.split(' ')) {
                const message = id ? root.getElementById(id) : null
                if (message) {
                    regions.add(message)
                }
            }
        }
    })
    for (const scope of scopes) {
        observer.observe(scope instanceof Document ? scope.body : scope, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['aria-invalid'],
        })
    }
    const heard = () => {
        const announced: string[] = []
        for (const region of regions) {
            if (
                !region.isConnected ||
                region.closest(CHAT_LOG) ||
                isExcluded(region, rules)
            ) {
                continue
            }
            const text = filteredText(region, rules, MAX_CHARS)
            if (text && !said.has(text) && !announced.includes(text)) {
                announced.push(text)
            }
            if (announced.length === MAX_ANNOUNCEMENTS) {
                break
            }
        }
        return announced
    }
    return {
        said: heard,
        stop() {
            observer.disconnect()
            return heard()
        },
    }
}
